import { useState, type FormEvent } from "react";
import { categories } from "@/mocks/categories";

const SUBMIT_ADDR = "https://readdy.ai/api/form/dain41jp14a1h3mr77d0";

type Status = "idle" | "loading" | "success" | "error";

export default function SubscribeForm() {
  const [status, setStatus] = useState<Status>("idle");
  const [message, setMessage] = useState("");

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const form = event.currentTarget;
    const formData = new FormData(form);
    const honeypot = String(formData.get("phone_alt") || "").trim();

    if (honeypot) {
      setStatus("success");
      setMessage("订阅成功，每日热榜会发送到你的邮箱。");
      form.reset();
      return;
    }

    setStatus("loading");
    setMessage("");

    const payload = new URLSearchParams();
    formData.forEach((value, key) => {
      if (key === "phone_alt") return;
      payload.append(key, String(value));
    });

    try {
      const response = await fetch(SUBMIT_ADDR, {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: payload.toString(),
      });
      const responseText = await response.text();
      let parsed: {
        code?: string;
        message?: string;
        meta?: { message?: string; detail?: string };
      } | null = null;
      try {
        parsed = JSON.parse(responseText);
      } catch {
        parsed = null;
      }
      const serverMsg =
        parsed?.meta?.message ||
        parsed?.message ||
        parsed?.meta?.detail ||
        responseText ||
        "";
      const isSpam = typeof serverMsg === "string" && serverMsg.toLowerCase().includes("spam");

      if (response.ok && parsed?.code === "OK" && !isSpam) {
        setStatus("success");
        setMessage("订阅成功，每日热榜会发送到你的邮箱。");
        form.reset();
      } else {
        setStatus("error");
        setMessage(serverMsg || "提交失败，请稍后重试。");
      }
    } catch {
      setStatus("error");
      setMessage("网络异常，请稍后重试。");
    }
  };

  return (
    <form
      data-readdy-form
      onSubmit={handleSubmit}
      className="rounded-lg border border-background-200 bg-background-50 p-5 md:p-6"
    >
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
        <label className="flex flex-col gap-2">
          <span className="text-xs font-medium text-foreground-600">邮箱地址</span>
          <input
            type="email"
            name="email"
            required
            placeholder="you@example.com"
            className="h-11 rounded-md border border-background-300 bg-background-50 px-3 text-sm text-foreground-950 outline-none transition-colors placeholder:text-foreground-400 focus:border-primary-400 focus:ring-2 focus:ring-primary-200"
          />
        </label>
        <label className="flex flex-col gap-2">
          <span className="text-xs font-medium text-foreground-600">最想看的栏目</span>
          <select
            name="topic"
            defaultValue="all"
            className="h-11 rounded-md border border-background-300 bg-background-50 px-3 text-sm text-foreground-950 outline-none transition-colors focus:border-primary-400 focus:ring-2 focus:ring-primary-200"
          >
            <option value="all">全部栏目</option>
            {categories.map((c) => (
              <option key={c.slug} value={c.slug}>
                {c.name}
              </option>
            ))}
          </select>
        </label>
      </div>

      <input
        type="text"
        name="phone_alt"
        tabIndex={-1}
        autoComplete="off"
        aria-hidden="true"
        readOnly
        className="field-extra-note"
      />

      <button
        type="submit"
        disabled={status === "loading"}
        className="mt-4 flex w-full items-center justify-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-6 py-3 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600 disabled:cursor-not-allowed disabled:opacity-70 cursor-pointer"
      >
        <i className={status === "loading" ? "ri-loader-4-line animate-spin" : "ri-mail-send-line"}></i>
        {status === "loading" ? "提交中..." : "免费订阅吃瓜日报"}
      </button>

      {status === "success" && (
        <p className="mt-3 flex items-center gap-2 text-sm text-primary-700">
          <i className="ri-checkbox-circle-line"></i>
          {message}
        </p>
      )}
      {status === "error" && (
        <p className="mt-3 flex items-center gap-2 text-sm text-secondary-700">
          <i className="ri-error-warning-line"></i>
          {message}
        </p>
      )}
      <p className="mt-3 text-xs leading-6 text-foreground-500">
        每天一封，包含热榜前三与深度专题，不含广告，可随时退订。
      </p>
    </form>
  );
}